import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Link } from "wouter";

import { api, type HorseSummary } from "@/api";
import {
  countdown,
  EmptyState,
  ErrorState,
  HorsePortrait,
  Skeleton,
  TierStars,
} from "@/components/ui";
import { trackTitle } from "@/features/racing/venue";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";

export function HomeDiscovery({ now }: { now: number }) {
  const prefetchRoute = useRoutePrefetch();
  const horses = useQuery({
    queryKey: ["horses", "in-form", 5],
    queryFn: () => api.inForm(5),
    refetchInterval: 30_000,
  });

  return (
    <section
      className="home-section home-discovery"
      aria-labelledby="home-in-form"
    >
      <header className="home-section__heading">
        <div>
          <span className="home-eyebrow">Beyond the finish line</span>
          <h2 id="home-in-form">Horses in form</h2>
        </div>
        <Link className="home-text-link" href="/app/leaderboards">
          Standings <ArrowUpRight aria-hidden size={16} />
        </Link>
      </header>
      {horses.isPending ? (
        <Skeleton rows={5} variant="horse-rail" />
      ) : horses.error ? (
        <ErrorState error={horses.error} retry={() => void horses.refetch()} />
      ) : horses.data.horses.length ? (
        <div className="horse-card-rail home-horse-rail">
          {horses.data.horses.map((horse) => (
            <HorseDiscoveryCard
              key={horse.horseId}
              horse={horse}
              now={now}
              onIntent={() =>
                prefetchRoute(`/app/player/horse/${horse.horseId}`)
              }
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title="Form follows the racing"
          detail="Horse form will appear here as results become official."
        />
      )}
    </section>
  );
}

function HorseDiscoveryCard({
  horse,
  now,
  onIntent,
}: {
  horse: HorseSummary;
  now: number;
  onIntent: () => void;
}) {
  const winRate = horse.career.races
    ? (horse.career.wins / horse.career.races) * 100
    : 0;
  return (
    <Link
      className="discovery-horse-card"
      href={`/app/player/horse/${horse.horseId}`}
      onFocus={onIntent}
      onMouseEnter={onIntent}
    >
      <HorsePortrait compact horse={horse} />
      <div className="discovery-horse-card__body">
        <strong>{horse.name}</strong>
        <TierStars rating={horse.starRating} tier={horse.tier} />
        <span>{trackTitle(horse)}</span>
        <div>
          <small>{horse.career.races} races</small>
          <small>{winRate.toFixed(1)}% wins</small>
        </div>
        <span>
          {horse.nextRaceAt
            ? `Next in ${countdown(horse.nextRaceAt, now)}`
            : "Between races"}
        </span>
      </div>
    </Link>
  );
}
